import React, { useMemo, useState } from 'react';
import { safeHTML } from '../../../../utils/safeHTML';
import { compilationScrollbarSettings } from '../../../../constants/copyright';
import { Scrollbar } from 'react-scrollbars-custom';
import { useUser } from '../../../../redux/reducer/user';
import { CSSTransition } from 'react-transition-group';
import { useContent } from '../../../../redux/reducer/content';
import GameResultModalRatingItem from './GameResultModalRatingItem';
import { useTranslation } from '../../../../translations';

const GameResultModalRating = React.forwardRef(function GameResultModalRating(
  { title, gameType, count, callbacks, userScore },
  ref,
) {
  const { profile } = useUser();
  const { gameRating } = useContent();
  const { t } = useTranslation();

  const [isVisible, setIsVisible] = useState(true);

  const ratingInfo = useMemo(() => {
    const baseRating = (gameRating[gameType] ?? []).slice(0, count);
    const storedUserRating = gameRating?.[`user${gameType}`] ?? {};

    if (!userScore || !profile?.uid) {
      return {
        activeRatings: {
          user: storedUserRating,
          rating: baseRating,
        },
      };
    }

    const existingUserInList = baseRating.find((item) => item?.uid === profile.uid);
    const existingScore = Number(existingUserInList?.score ?? storedUserRating?.score ?? 0);
    const currentScore = Number(userScore ?? 0);

    if (currentScore <= existingScore) {
      const ratingWithPositions = baseRating.map((item, index) => ({
        ...item,
        position: index + 1,
      }));

      const finalUserItem = ratingWithPositions.find((item) => item?.uid === profile.uid) || storedUserRating || {};

      return {
        activeRatings: {
          user: finalUserItem,
          rating: ratingWithPositions,
        },
      };
    }

    const updatedUserItem = {
      ...(existingUserInList || storedUserRating || {}),
      uid: profile.uid,
      username: existingUserInList?.username ?? storedUserRating?.username ?? profile.username,
      iconId: existingUserInList?.iconId ?? storedUserRating?.iconId ?? profile.iconId,
      score: currentScore,
    };

    const others = baseRating.filter((item) => item?.uid !== updatedUserItem.uid);

    if (!existingUserInList) {
      const minScoreInTop = others.length ? Math.min(...others.map((item) => Number(item?.score ?? 0))) : 0;

      if (others.length >= count && currentScore <= minScoreInTop) {
        const ratingWithPositions = baseRating.map((item, index) => ({
          ...item,
          position: index + 1,
        }));

        return {
          activeRatings: {
            user: {
              ...storedUserRating,
              score: Math.max(existingScore, currentScore),
            },
            rating: ratingWithPositions,
          },
        };
      }
    }

    const insertIndex = others.findIndex((item) => Number(item?.score ?? 0) < currentScore);

    const updatedRating =
      insertIndex === -1
        ? [...others, updatedUserItem]
        : [...others.slice(0, insertIndex), updatedUserItem, ...others.slice(insertIndex)];

    const ratingWithPositions = updatedRating.slice(0, count).map((item, index) => ({
      ...item,
      position: index + 1,
    }));

    const finalUserItem = ratingWithPositions.find((item) => item?.uid === updatedUserItem.uid) ?? updatedUserItem;

    return {
      activeRatings: {
        user: finalUserItem,
        rating: ratingWithPositions,
      },
    };
  }, [gameRating, gameType, count, userScore, profile]);

  const isHaveRating = useMemo(() => {
    const { activeRatings } = ratingInfo ?? {};
    return [/*activeRatings?.user, */ activeRatings?.rating].every((item) =>
      Array.isArray(item) ? !!item?.length : !!Object.values(item ?? {}).length,
    );
  }, [ratingInfo]);

  return (
    isHaveRating && (
      <div className={'game-result-modal__rating'}>
        <h3 className={'game-result-modal__rating-title'}>{safeHTML(t(title))}</h3>
        <div className={'game-result-modal__rating-block'}>
          <Scrollbar {...compilationScrollbarSettings}>
            {ratingInfo?.activeRatings?.rating?.map((item, index) => (
              <GameResultModalRatingItem
                isUser={item?.uid === profile?.uid}
                key={`item-${index}`}
                setIsVisible={setIsVisible}
                {...item}
              />
            ))}
          </Scrollbar>

          <CSSTransition
            in={!isVisible && !!Object.values(ratingInfo?.activeRatings?.user ?? {}).length}
            timeout={300}
            mountOnEnter={true}
            unmountOnExit={true}
            classNames={'game-result-modal__rating-item'}
          >
            <GameResultModalRatingItem isReservation={true} {...ratingInfo?.activeRatings?.user} />
          </CSSTransition>
        </div>
      </div>
    )
  );
});
export default GameResultModalRating;
GameResultModalRating.propTypes = {};
